/**
 * @voltxampere/spice — MOSFET codec (M)
 *
 * Berkeley SPICE3 syntax:
 *   M{name} ND NG NS NB MODEL_NAME [W=...] [L=...]
 *
 * Examples:
 *   M1 d g s 0 NMOS_DEFAULT W=1u L=180n
 *   M2 d g s b PMOS_DEFAULT
 *   M3 d g s b IRF540
 *
 * Polarity dispatch via model name. Substrate is mandatory in
 * SPICE3 syntax — Sprint 16 accepts a 3-node (no substrate)
 * shorthand commonly seen in tutorials by treating the missing
 * substrate as ground.
 *
 * Schema mapping: `type: "nmos"` or `type: "pmos"` (split symbols),
 * params: `{ model, W?, L? }`.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ok, err, } from "./_base.js";
import { parseSpiceNumber, formatSpiceValue } from "../units.js";
import { numericParam, stringParam } from "./_param-helpers.js";
const NMOS_MODELS = new Set(["NMOS", "NMOS_DEFAULT", "IRF540", "IRLZ44", "2N7000", "BSS138"]);
const PMOS_MODELS = new Set(["PMOS", "PMOS_DEFAULT", "IRF9540", "IRF4905", "BS250"]);
function classifyMosfetModel(model) {
    const upper = model.toUpperCase();
    if (PMOS_MODELS.has(upper) || upper.startsWith("PMOS"))
        return "pmos";
    if (NMOS_MODELS.has(upper) || upper.startsWith("NMOS"))
        return "nmos";
    return "nmos";
}
function looksLikeKnownMosfetModel(token) {
    const upper = token.toUpperCase();
    if (upper.startsWith("NMOS") || upper.startsWith("PMOS"))
        return true;
    if (NMOS_MODELS.has(upper) || PMOS_MODELS.has(upper))
        return true;
    if (upper.startsWith("IRF") || upper.startsWith("IRL") || upper.startsWith("BS"))
        return true;
    return false;
}
export const mosfetCodec = {
    spiceLetter: "M",
    schemaTypes: ["nmos", "pmos", "mosfet"],
    examples: ["M1 d g s 0 NMOS_DEFAULT W=1u L=180n", "M2 d g s b PMOS_DEFAULT"],
    parse(card, _ctx) {
        if (card.tokens.length < 4) {
            return err("M needs at least 3 nodes and a model name");
        }
        // Strategy: find the LAST non-W=/L= token — that's the model.
        // Everything before is nodes (3 or 4 of them). This avoids
        // mistaking a single-letter bulk node ("b") for a model.
        let modelIdx = -1;
        for (let i = card.tokens.length - 1; i >= 3; i--) {
            const tok = card.tokens[i];
            if (tok === undefined)
                continue;
            if (tok.includes("="))
                continue;
            modelIdx = i;
            break;
        }
        if (modelIdx === -1) {
            return err("M missing model name after nodes");
        }
        // Sanity: confirm the candidate is plausibly a model name.
        const candidate = card.tokens[modelIdx];
        if (candidate === undefined || !looksLikeKnownMosfetModel(candidate)) {
            // Fall back: accept anything alphabetic that's not a single
            // node letter (e.g. "NMOS" works, "b" doesn't).
            if (candidate === undefined || candidate.length < 2) {
                return err("M model name unrecognized");
            }
        }
        const nodes = card.tokens.slice(0, modelIdx);
        if (nodes.length < 3 || nodes.length > 4) {
            return err("M needs 3 or 4 nodes (D G S [B])");
        }
        const dgsNodes = nodes.slice(0, 3);
        if (dgsNodes.length !== 3)
            return err("M missing D / G / S");
        const modelRaw = card.tokens[modelIdx];
        if (modelRaw === undefined)
            return err("M model token vanished");
        const polarity = classifyMosfetModel(modelRaw);
        const params = { model: modelRaw };
        if (nodes.length === 4 && nodes[3] !== undefined) {
            params.bulk = nodes[3];
        }
        // Optional W=, L= clauses.
        for (let i = modelIdx + 1; i < card.tokens.length; i++) {
            const tok = card.tokens[i];
            if (tok === undefined)
                continue;
            const wMatch = /^W=(.+)$/i.exec(tok);
            const lMatch = /^L=(.+)$/i.exec(tok);
            if (wMatch?.[1] !== undefined) {
                const w = parseSpiceNumber(wMatch[1]);
                if (w !== null)
                    params.W = w;
            }
            if (lMatch?.[1] !== undefined) {
                const l = parseSpiceNumber(lMatch[1]);
                if (l !== null)
                    params.L = l;
            }
        }
        const component = {
            id: card.identifier,
            type: polarity,
            position: { x: 0, y: 0 },
            rotation: 0,
            flipped: false,
            label: card.identifier,
            params,
            terminals: [],
        };
        return ok(component, dgsNodes);
    },
    emit(component, terminals) {
        const fallback = component.type === "pmos" ? "PMOS_DEFAULT" : "NMOS_DEFAULT";
        const model = stringParam(component.params.model, fallback);
        const bulk = stringParam(component.params.bulk, "0");
        const w = numericParam(component.params.W, NaN);
        const l = numericParam(component.params.L, NaN);
        const [d, g, s] = terminals;
        let line = `${component.id} ${d ?? "0"} ${g ?? "0"} ${s ?? "0"} ${bulk} ${model}`;
        if (Number.isFinite(w))
            line += ` W=${formatSpiceValue(w)}`;
        if (Number.isFinite(l))
            line += ` L=${formatSpiceValue(l)}`;
        return line;
    },
    owns(schemaType) {
        return schemaType === "nmos" || schemaType === "pmos" || schemaType === "mosfet";
    },
};
//# sourceMappingURL=mosfet.js.map